/**
 * PhotoGallery — admin operational image viewer.
 *
 * Design intent: this is a DEALERSHIP ACQUISITION WORKSTATION, not a social
 * gallery. Sourcing executives need to evaluate paint, dents, scratches, tyres,
 * panel gaps remotely — without asking the seller to resend on WhatsApp.
 *
 * Capabilities:
 *  - Responsive grid (4-col desktop, 2-col mobile) with image count visible
 *  - First photo marked COVER
 *  - Lazy-loaded thumbnails (no full-res blast on initial render)
 *  - Click → fullscreen lightbox with zoom + arrow-key + swipe navigation
 *  - Per-photo "Download" button
 *  - "Download All (ZIP)" → backend streams QDRIVES_<REG>_<DATE>.zip
 *  - Graceful fallback on broken signed URL (icon + label, not a ghosted box)
 *
 * Future extensibility (NOT IMPLEMENTED YET — structure ready):
 *   Photo objects can carry a `tag` field later: Exterior / Interior / Damage
 *   / RC / Odometer / Tyres. The component reads photo.tag if present and
 *   shows it as a small overlay chip, but no tagging UI is exposed.
 *
 * Contract with backend:
 *   - Vehicle response should include `photos_signed: [{ url, tag? }]`.
 *     Each `url` is an HMAC-signed `/api/files/...?sig=&exp=` path → no JWT
 *     in the URL. Resolved with `resolvePhotoUrl()`.
 *   - Falls back to `vehicle.photos: [url|string]` if `photos_signed` missing,
 *     but signed is always preferred (prevents JWT-in-URL leak + token rotation
 *     bugs).
 */
import React, { useEffect, useState, useCallback } from "react";
import { resolvePhotoUrl } from "@/lib/img";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  CaretLeft, CaretRight, X, MagnifyingGlassPlus, MagnifyingGlassMinus,
  DownloadSimple, Archive, ImageSquare,
} from "@phosphor-icons/react";

/**
 * Normalize the photos source for this vehicle.
 * Returns an array of { url, tag? } where url is a directly-renderable string.
 */
function normalizePhotos(vehicle) {
  if (!vehicle) return [];
  const signed = vehicle.photos_signed;
  if (Array.isArray(signed) && signed.length) {
    return signed.map((p) => ({
      url: resolvePhotoUrl(p),
      tag: p?.tag || null,
    }));
  }
  const raw = vehicle.photos;
  if (Array.isArray(raw) && raw.length) {
    return raw.map((p) => ({ url: resolvePhotoUrl(p), tag: null }));
  }
  return [];
}

export default function PhotoGallery({ vehicle, testid = "photo-gallery" }) {
  const photos = normalizePhotos(vehicle);
  const [lightbox, setLightbox] = useState(null); // index or null
  const [zipping, setZipping] = useState(false);

  const downloadAll = async () => {
    if (!vehicle?.id) return;
    setZipping(true);
    const t = toast.loading("Preparing photo archive…");
    try {
      const res = await api.get(`/admin/vehicles/${vehicle.id}/photos.zip`, {
        responseType: "blob",
        timeout: 60000,
      });
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const reg = (vehicle.registration_number || "no-reg").replace(/[^A-Z0-9_-]/gi, "_").toUpperCase();
      const date = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      const a = document.createElement("a");
      a.href = url;
      a.download = `QDRIVES_${reg}_${date}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${photos.length} photos`, { id: t });
    } catch (e) {
      toast.error(`Archive failed: ${e?.response?.status || "network"}`, { id: t });
    } finally {
      setZipping(false);
    }
  };

  if (photos.length === 0) {
    return (
      <section data-testid={testid}>
        <div className="qd-overline mb-2">Photos</div>
        <div className="bg-[#0E0E0E] border border-[#1F1F1F] py-8 text-center">
          <ImageSquare size={28} className="mx-auto text-white/25 mb-2" />
          <div className="text-[12px] text-white/45">No photos uploaded by seller</div>
        </div>
      </section>
    );
  }

  return (
    <section data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="qd-overline">Photos · {photos.length}</div>
        <button
          onClick={downloadAll}
          disabled={zipping}
          data-testid="photo-download-all"
          className="btn-secondary text-[10px] py-1.5 px-2.5 inline-flex items-center gap-1.5"
        >
          <Archive size={12} weight="bold" />
          {zipping ? "Zipping…" : "Download All"}
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5">
        {photos.map((p, i) => (
          <PhotoTile
            key={`${i}-${p.url}`}
            photo={p}
            index={i}
            isCover={i === 0}
            onOpen={() => setLightbox(i)}
            testid={`${testid}-tile-${i}`}
          />
        ))}
      </div>

      {lightbox !== null && (
        <Lightbox
          photos={photos}
          startIndex={lightbox}
          vehicleReg={vehicle.registration_number}
          onClose={() => setLightbox(null)}
        />
      )}
    </section>
  );
}

function PhotoTile({ photo, index, isCover, onOpen, testid }) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={testid}
      className="relative aspect-square bg-[#0E0E0E] border border-[#1F1F1F] overflow-hidden group hover:border-[#FF1E1E]/40 transition-colors"
    >
      {!failed ? (
        <>
          {!loaded && <div className="absolute inset-0 animate-pulse bg-[#1A1A1A]" />}
          <img
            src={photo.url}
            alt={`Vehicle photo ${index + 1}`}
            loading="lazy"
            className={`w-full h-full object-cover transition-opacity duration-200 ${loaded ? "opacity-100" : "opacity-0"}`}
            onLoad={() => setLoaded(true)}
            onError={() => { setFailed(true); setLoaded(true); }}
          />
        </>
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-white/30">
          <ImageSquare size={20} />
          <span className="text-[9px] tracking-widest uppercase">Failed</span>
        </div>
      )}

      {isCover && !failed && (
        <span className="absolute top-1 left-1 bg-[#FF1E1E] text-white text-[8px] font-bold tracking-widest px-1.5 py-[1px]">COVER</span>
      )}
      {photo.tag && (
        <span className="absolute bottom-1 left-1 bg-black/70 text-white text-[9px] tracking-wide px-1.5 py-[1px]">{photo.tag}</span>
      )}
      <span className="absolute bottom-1 right-1 bg-black/55 text-white/85 text-[10px] font-mono-data px-1.5">{index + 1}</span>
    </button>
  );
}

/**
 * Lightbox — fullscreen viewer with zoom + arrow/swipe navigation.
 * - Arrow keys: ← → between photos, Esc to close.
 * - Mouse: scroll wheel zooms, drag-pan when zoomed.
 * - Touch: swipe between photos when not zoomed; pinch-to-zoom natively via CSS.
 */
function Lightbox({ photos, startIndex, vehicleReg, onClose }) {
  const [idx, setIdx] = useState(startIndex);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(null); // { startX, startY, offsetX, offsetY }

  const photo = photos[idx];
  const total = photos.length;

  const goNext = useCallback(() => {
    setZoom(1); setOffset({ x: 0, y: 0 });
    setIdx((i) => (i + 1) % total);
  }, [total]);
  const goPrev = useCallback(() => {
    setZoom(1); setOffset({ x: 0, y: 0 });
    setIdx((i) => (i - 1 + total) % total);
  }, [total]);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") goNext();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "+" || e.key === "=") setZoom((z) => Math.min(4, z + 0.5));
      else if (e.key === "-" || e.key === "_") {
        setZoom((z) => {
          const next = Math.max(1, z - 0.5);
          if (next === 1) setOffset({ x: 0, y: 0 });
          return next;
        });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, goNext, goPrev]);

  // Touch swipe (only when not zoomed)
  const touchStart = React.useRef(null);
  const onTouchStart = (e) => {
    if (zoom > 1) return;
    touchStart.current = { x: e.touches[0].clientX, t: Date.now() };
  };
  const onTouchEnd = (e) => {
    if (!touchStart.current || zoom > 1) return;
    const dx = e.changedTouches[0].clientX - touchStart.current.x;
    if (Math.abs(dx) > 60) {
      if (dx < 0) goNext();
      else goPrev();
    }
    touchStart.current = null;
  };

  // Wheel zoom
  const onWheel = (e) => {
    e.preventDefault();
    setZoom((z) => Math.max(1, Math.min(4, z + (e.deltaY > 0 ? -0.2 : 0.2))));
  };

  // Drag-to-pan when zoomed
  const onMouseDown = (e) => {
    if (zoom <= 1) return;
    setDragging({ startX: e.clientX, startY: e.clientY, offsetX: offset.x, offsetY: offset.y });
  };
  const onMouseMove = (e) => {
    if (!dragging) return;
    setOffset({
      x: dragging.offsetX + (e.clientX - dragging.startX),
      y: dragging.offsetY + (e.clientY - dragging.startY),
    });
  };
  const onMouseUp = () => setDragging(null);

  const downloadOne = async () => {
    try {
      const res = await fetch(photo.url, { credentials: "omit" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const reg = (vehicleReg || "photo").replace(/[^A-Z0-9_-]/gi, "_").toUpperCase();
      const a = document.createElement("a");
      a.href = url;
      a.download = `QDRIVES_${reg}_${(idx + 1).toString().padStart(2, "0")}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`Download failed: ${e.message}`);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex flex-col"
      data-testid="photo-lightbox"
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-white/8">
        <div className="font-mono-data text-[11px] tracking-widest text-white/65">
          {idx + 1} / {total} {vehicleReg && ` · ${vehicleReg}`}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setZoom((z) => Math.max(1, z - 0.5))}
            data-testid="lightbox-zoom-out"
            disabled={zoom <= 1}
            className="btn-secondary p-2 disabled:opacity-30"
            title="Zoom out (-)"
          >
            <MagnifyingGlassMinus size={14} weight="bold" />
          </button>
          <div className="font-mono-data text-[10px] text-white/55 w-10 text-center">{Math.round(zoom * 100)}%</div>
          <button
            onClick={() => setZoom((z) => Math.min(4, z + 0.5))}
            data-testid="lightbox-zoom-in"
            disabled={zoom >= 4}
            className="btn-secondary p-2 disabled:opacity-30"
            title="Zoom in (+)"
          >
            <MagnifyingGlassPlus size={14} weight="bold" />
          </button>
          <button
            onClick={downloadOne}
            data-testid="lightbox-download"
            className="btn-secondary p-2"
            title="Download this photo"
          >
            <DownloadSimple size={14} weight="bold" />
          </button>
          <button
            onClick={onClose}
            data-testid="lightbox-close"
            className="btn-secondary p-2"
            title="Close (Esc)"
          >
            <X size={14} weight="bold" />
          </button>
        </div>
      </div>

      {/* Stage */}
      <div
        className="flex-1 flex items-center justify-center overflow-hidden select-none relative"
        onWheel={onWheel}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
        style={{ cursor: zoom > 1 ? (dragging ? "grabbing" : "grab") : "default" }}
      >
        <img
          src={photo.url}
          alt={`Photo ${idx + 1}`}
          draggable={false}
          className="max-w-full max-h-full object-contain transition-transform"
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
            transition: dragging ? "none" : "transform 0.15s ease-out",
          }}
        />
      </div>

      {/* Footer prev/next */}
      {total > 1 && (
        <>
          <button
            onClick={goPrev}
            data-testid="lightbox-prev"
            className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/55 hover:bg-black/80 p-3 text-white"
            aria-label="Previous photo"
          >
            <CaretLeft size={20} weight="bold" />
          </button>
          <button
            onClick={goNext}
            data-testid="lightbox-next"
            className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/55 hover:bg-black/80 p-3 text-white"
            aria-label="Next photo"
          >
            <CaretRight size={20} weight="bold" />
          </button>
        </>
      )}
    </div>
  );
}
